import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const PromoCodeFormModal = ({ isOpen, onClose, onSubmit, promoCode, isEditMode }) => {
  const [formData, setFormData] = useState({
    code: '',
    quantite: 0,
    montant: 0,
    active: true
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);

  // Initialize form
  useEffect(() => {
    if (isEditMode && promoCode) {
      setFormData({
        code: promoCode.code || '',
        quantite: promoCode.quantite || 0,
        montant: promoCode.montant || 0,
        active: promoCode.active ?? true
      });
    } else {
      setFormData({
        code: '',
        quantite: 0,
        montant: 0,
        active: true
      });
    }
    
    setErrors({});
    setFocusedField(null);
  }, [isEditMode, promoCode, isOpen]);

  const validateForm = useCallback(() => {
    const newErrors = {};
    
    if (!formData.code) {
      newErrors.code = 'Le code promo est obligatoire';
    } else if (formData.code.length < 3) {
      newErrors.code = 'Le code doit contenir au moins 3 caractères';
    }
    
    if (formData.montant <= 0) {
      newErrors.montant = 'Le montant doit être supérieur à 0';
    }
    
    if (formData.quantite < 0) {
      newErrors.quantite = 'La quantité ne peut pas être négative';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  }, [errors]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // CRITICAL: Only include the specific fields we want to send
      // NEVER include nbrUsed in any scenario
      const submitData = {
        code: formData.code,
        quantite: Number(formData.quantite),
        montant: Number(formData.montant),
        active: formData.active
      };
      
      // Only add ID for edit mode - nothing else from the original promoCode object
      if (isEditMode && promoCode) {
        submitData.id = promoCode.id;
      }
      
      // DO NOT spread any other properties from promoCode
      // DO NOT include nbrUsed, updatedDatetime, or any other fields
      
      
      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onSubmit, onClose, validateForm, isEditMode, promoCode]);

  const handleFocus = useCallback((fieldName) => setFocusedField(fieldName), []);
  const handleBlur = useCallback(() => setFocusedField(null), []);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        {/* Backdrop */}
        <div className="flex min-h-screen items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-gray-900/75 backdrop-blur-sm"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ scale: 0.98, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.98, opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="relative w-full max-w-2xl mx-auto bg-white shadow-xl rounded-xl overflow-hidden"
          >
            {/* Header */}
            <div className="relative bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-5">
              {/* Header Content */}
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-emerald-500/20 rounded-lg border border-emerald-400/20">
                    <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      {isEditMode ? 'Modifier le code promo' : 'Créer un code promo'}
                    </h2>
                    <p className="text-slate-300 text-sm">
                      {isEditMode ? 'Modifiez les détails de votre code promotionnel' : 'Configurez votre nouveau code promotionnel'}
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-300 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Form Content */}
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Code Field */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Code promotionnel
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="code"
                        value={formData.code}
                        onChange={handleChange}
                        onFocus={() => handleFocus('code')}
                        onBlur={handleBlur}
                        disabled={isEditMode}
                        placeholder="Ex: SUMMER2024"
                        className={`block w-full px-4 py-3 text-lg font-mono tracking-wider rounded-lg border-2 transition-colors duration-150 ${
                          isEditMode 
                            ? 'bg-gray-50 border-gray-200 cursor-not-allowed' 
                            : focusedField === 'code'
                              ? 'border-blue-500 bg-blue-50/30'
                              : errors.code
                                ? 'border-red-500 bg-red-50/30'
                                : 'border-gray-300 hover:border-gray-400 bg-white'
                        }`}
                      />
                      {!isEditMode && (
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                        </div>
                      )}
                    </div>
                    {errors.code && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.code}
                      </p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      Ce code sera utilisé par vos clients pour bénéficier de la réduction
                    </p>
                  </div>

                  {/* Montant (Amount) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Montant de réduction (TND)
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="montant"
                        value={formData.montant}
                        onChange={handleChange}
                        onFocus={() => handleFocus('montant')}
                        onBlur={handleBlur}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className={`block w-full px-4 py-3 pr-12 rounded-lg border-2 transition-colors duration-150 ${
                          focusedField === 'montant'
                            ? 'border-blue-500 bg-blue-50/30'
                            : errors.montant
                              ? 'border-red-500 bg-red-50/30'
                              : 'border-gray-300 hover:border-gray-400 bg-white'
                        }`}
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                        <span className="text-gray-500 text-lg font-medium">TND</span>
                      </div>
                    </div>
                    {errors.montant && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.montant}
                      </p>
                    )}
                  </div>

                  {/* Quantite (Usage Limit) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantité disponible
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="quantite"
                        value={formData.quantite}
                        onChange={handleChange}
                        onFocus={() => handleFocus('quantite')}
                        onBlur={handleBlur}
                        min="0"
                        placeholder="0 = illimité"
                        className={`block w-full px-4 py-3 rounded-lg border-2 transition-colors duration-150 ${
                          focusedField === 'quantite'
                            ? 'border-blue-500 bg-blue-50/30'
                            : errors.quantite
                              ? 'border-red-500 bg-red-50/30'
                              : 'border-gray-300 hover:border-gray-400 bg-white'
                        }`}
                      />
                    </div>
                    {errors.quantite && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.quantite}
                      </p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      Nombre de fois que ce code peut être utilisé (0 pour illimité)
                    </p>
                  </div>

                  {/* Status Toggle */}
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div>
                        <h4 className="font-medium text-gray-900">Statut du code promo</h4>
                        <p className="text-sm text-gray-500">
                          {formData.active ? 'Le code est actif et utilisable' : 'Le code est désactivé'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="active"
                          checked={formData.active}
                          onChange={handleChange}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>

                  {/* Display usage info in edit mode - READ ONLY */}
                  {isEditMode && promoCode && (
                    <div className="md:col-span-2">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          Statistiques d'utilisation (lecture seule)
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-blue-600 font-medium">Utilisations:</span>
                            <span className="ml-2 text-blue-900 font-bold">{promoCode.nbrUsed || 0}</span>
                          </div>
                          <div>
                            <span className="text-blue-600 font-medium">Dernière maj:</span>
                            <span className="ml-2 text-blue-900">
                              {promoCode.updatedDatetime ? new Date(promoCode.updatedDatetime).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                        </div>
                        <p className="mt-2 text-xs text-blue-600">
                          💡 Le nombre d'utilisations ne peut pas être modifié manuellement
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors duration-150"
              >
                Annuler
              </button>
              
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="relative px-8 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/25 disabled:opacity-70 disabled:cursor-not-allowed transition-colors duration-150"
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {isEditMode ? 'Mise à jour...' : 'Création...'}
                  </span>
                ) : (
                  <span className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {isEditMode ? 'Mettre à jour' : 'Créer le code'}
                  </span>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PromoCodeFormModal;